import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { nuvraAuth } from "@/auth/nuvraAuth";
import { Loader2, AlertCircle } from "lucide-react";
import { NuvraLogo } from "@/components/NuvraLogo";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Landing pad for the passwordless sign-in link.
 *
 * The emailed link points here with ?token=... . This page claims the one-time
 * token via Nuvra's own /api/auth/magic-link/verify, which mints and returns the
 * Supabase session the client installs (nuvraAuth.verifyMagicLink). On success
 * the AuthContext picks up the new session and we route to the dashboard.
 */
export default function MagicLinkPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const token = params.get("token");
    if (!token) {
      setError(t("magicLink.missingToken"));
      return;
    }

    void (async () => {
      const result = await nuvraAuth.verifyMagicLink({ token });
      if (result.success) {
        navigate("/dashboard", { replace: true });
      } else {
        setError(result.error ?? t("magicLink.invalid"));
      }
    })();
  }, [params, navigate, t]);

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="text-center space-y-4 max-w-sm px-4">
        <div className="flex items-center justify-center gap-2">
          <NuvraLogo size={28} />
          <span className="font-bold text-lg text-foreground">Nuvra</span>
        </div>
        {error ? (
          <>
            <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={() => navigate("/login", { replace: true })}>
              {t("magicLink.backToSignIn")}
            </Button>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">{t("magicLink.signingIn")}</p>
          </>
        )}
      </div>
    </div>
  );
}
